"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "Are your water system labels UV-resistant?",
  answer: "Yes. We utilize industrial-grade materials specifically engineered for harsh outdoor environments. Our labeling solutions are UV-stabilized and chemical-resistant, ensuring they maintain high legibility and structural integrity even under direct sunlight and exposure to water treatment chemicals."
}, {
  question: "Do you meet AWWA and OSHA standards?",
  answer: "Absolutely. We strictly adhere to AWWA (American Water Works Association) color-coding guidelines and OSHA safety labeling requirements. Every tag and sign we produce is designed to ensure compliance with local and federal safety regulations for water and wastewater facilities."
}, {
  question: "Can you handle custom pipe marking requirements?",
  answer: "We specialize in custom specifications. Whether you need specific flow direction arrows, unique facility identification codes, or custom sizing for large-diameter piping, our team works directly from your submittal packages to ensure perfect accuracy."
}, {
  question: "What materials do you recommend for corrosive environments?",
  answer: "For wastewater environments with high chemical exposure, we recommend our high-performance phenolic or stainless steel tags. These materials offer superior resistance to corrosion, moisture, and aggressive cleaning agents, providing a permanent solution for critical infrastructure."
}, {
  question: "How long is the typical lead time for large projects?",
  answer: "We prioritize efficiency for critical infrastructure projects. Standard orders are typically processed within 5 to 10 business days. For urgent facility upgrades or emergency repairs, we offer expedited production schedules to keep your project on track."
}, {
  question: "What makes your labeling solutions durable?",
  answer: "Our process goes beyond simple printing. We use deep-engraving and high-bond industrial adhesives that are tested for extreme temperature fluctuations and high-pressure washdowns. Our labels are built to last as long as the infrastructure they identify."
}, {
  question: "Do you provide submittal documentation?",
  answer: "Yes. We provide comprehensive submittal packages including material data sheets, color samples, and layout proofs for engineer approval. We ensure all documentation meets the rigorous standards required for municipal and industrial water projects."
}, {
  question: "Can you engrave specific equipment IDs?",
  answer: "We can engrave any alphanumeric sequence, including complex equipment IDs, valve numbers, and flow rates. Our precision laser engraving ensures that even the smallest text remains crisp and readable for the life of the equipment."
}];
export default function WaterWastewaterLabelingCommonQuestions() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2463" id="water-wastewater-labeling-common-questions" className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-24">
              <div className="font-mono text-xs tracking-widest uppercase text-primary mb-8">
                TECHNICAL FAQS / 03
              </div>

              <h2 className="font-default font-bold text-accent mb-10" style={{
              fontSize: "clamp(2.75rem, 5vw, 5rem)",
              lineHeight: "0.95",
              letterSpacing: "-0.03em"
            }}>
                Infrastructure
                <br />
                Labeling
                <br />
                Insights.
              </h2>

              <p className="font-sans text-base md:text-lg leading-relaxed mb-10" style={{
              maxWidth: "360px",
              color: "#A1A1AA"
            }}>
                We provide clarity on our industrial labeling standards. If you have specific project requirements or need technical guidance, our team is ready to assist.
              </p>

              
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Accordion type="single" collapsible defaultValue="item-0" className="flex flex-col gap-3">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-0 backdrop-blur-sm transition-all duration-300 group" style={{
              backgroundColor: "var(--card)",
              border: "1px solid var(--border)",
              boxShadow: "var(--shadow-md)"
            }}>
                  <AccordionTrigger className="hover:no-underline px-6 md:px-8 py-6 md:py-7 min-h-[96px] gap-6 [&>svg]:hidden group-hover:[border-color:var(--primary)]">
                    <div className="flex items-center gap-6 md:gap-8 flex-1 text-left">
                      <span className="font-default font-bold text-xs text-primary shrink-0 tracking-wider">
                        Q.{String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default font-medium text-base md:text-lg text-accent leading-snug">
                        {faq.question}
                      </span>
                    </div>
                    <Plus className="size-6 text-primary shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                  </AccordionTrigger>
                  <AccordionContent className="px-6 md:px-8 pb-8 pt-0">
                    <div className="pl-0 md:pl-[88px]">
                      <p className="font-sans text-base md:text-[1.0625rem]" style={{
                    lineHeight: "1.75",
                    maxWidth: "600px",
                    color: "#A1A1AA"
                  }}>
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}